import { useContext, useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  User,
  Heart,
  Stethoscope,
  CreditCard,
  Shield,
  CheckCircle,
} from "lucide-react";
import API from "../../services/interceptor";
import { AuthContext } from "../../context/AuthContext";

const PaymentComponent = ({
  amount,
  appointmentDetails = {},
  petDetails = {},
  doctorDetails = {},
  onPaymentSuccess,
  onPaymentFailure,
  formData,
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentData, setPaymentData] = useState(null);

  const { loginUser, userDetails, getUserDetails } = useContext(AuthContext);

  useEffect(() => {
    if (userDetails == null || Object.keys(userDetails).length === 0) {
      getUserDetails();
    }
  }, [getUserDetails, userDetails]);

  // Extract appointment details with fallbacks
  const {
    type = "consultation",
    date = "",
    time = "",
    notes = "",
    appointmentId = "",
  } = appointmentDetails;

  // Extract pet details with fallbacks
  const { name: petName = "Pet", species = "", breed = "" } = petDetails;

  // Extract doctor details with fallbacks
  const {
    name: doctorName = "Doctor",
    specialization = [],
    consultationFee = 1,
  } = doctorDetails;

  // Sample user data - replace with actual user data from your auth context
  const userId = loginUser.id; // Replace with actual user ID
  const purpose = type || "consultation";

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    setPaymentStatus("");
    setPaymentData(null);

    try {
      // Load Razorpay script
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        setPaymentStatus(
          "Failed to load Razorpay SDK. Please check your connection."
        );
        setLoading(false);
        return;
      }

      // Create order using axios
      const orderResponse = await API.post(
        "/api/payments/create-order",
        {
          amount: amount,
          currency: "INR",
          userId: userId,
          purpose: purpose,
          notes: `Payment for ${type} - ${petName} with Dr. ${doctorName}`,
          appointmentId: appointmentId,
          petId: petDetails._id,
          doctorId: doctorDetails._id,
          formData,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const orderData = orderResponse.data;

      if (!orderData.success) {
        setPaymentStatus(`Error: ${orderData.message}`);
        setLoading(false);
        return;
      }

      const { order_id, key_id, paymentRecordId } = orderData;

      // Razorpay options
      const options = {
        key: key_id,
        amount: amount * 100, // Amount in paise
        currency: "INR",
        name: "Animalia+ Veterinary",
        description: `${type} for ${petName}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment using axios
            const verifyResponse = await API.post(
              "/api/payments/verify-payment",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                paymentRecordId: paymentRecordId,
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const verifyData = verifyResponse.data;

            if (verifyData.success) {
              setPaymentStatus("Payment Successful! ✅");
              setPaymentData({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: amount,
                status: "Success",
              });

              // Call success callback if provided
              if (onPaymentSuccess) {
                onPaymentSuccess({
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                  amount: amount,
                  appointmentDetails,
                  petDetails,
                  doctorDetails,
                });
              }
            } else {
              setPaymentStatus("Payment verification failed! ❌");
              if (onPaymentFailure) {
                onPaymentFailure({ message: "Payment verification failed" });
              }
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            setPaymentStatus("Payment verification failed! ❌");
            if (onPaymentFailure) {
              onPaymentFailure({ message: "Payment verification failed" });
            }
          }
        },

        //  const userName = loginUser?.name
        //     const userEmail = loginUser?.email
        //     const contactNumber = loginUser?.userdetails?.contact?.mobile;

        prefill: {
          name: loginUser?.name,
          email: loginUser?.email,
          contact: userDetails?.userDetails?.contact?.mobile,
        },
        notes: {
          purpose: purpose,
          userId: userId,
          petName: petName,
          doctorName: doctorName,
          appointmentDate: date,
          appointmentTime: time,
        },
        theme: {
          color: "#4F46E5",
        },
        modal: {
          ondismiss: () => {
            setPaymentStatus("Payment cancelled by user");
            setLoading(false);
          },
        },
      };

      // Open Razorpay checkout
      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", (response) => {
        setPaymentStatus("Payment Failed! ❌");
        console.error("Payment failed:", response.error);
        if (onPaymentFailure) {
          onPaymentFailure({ message: "Payment failed" });
        }
      });

      rzp.open();
    } catch (error) {
      console.error("Error creating order:", error);

      // Handle axios error response
      if (error.response) {
        // Server responded with error status
        setPaymentStatus(
          `Error: ${error.response.data?.message || "Server error occurred"}`
        );
      } else if (error.request) {
        // Request was made but no response received
        setPaymentStatus("Network error. Please check your connection.");
      } else {
        // Something else happened
        setPaymentStatus("Error creating order. Please try again.");
      }

      if (onPaymentFailure) {
        onPaymentFailure({ message: "Error creating order" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Format appointment type for display
  const formatAppointmentType = (type) => {
    const types = {
      checkup: "General Checkup",
      vaccination: "Vaccination",
      surgery: "Surgery",
      consultation: "Consultation",
      emergency: "Emergency",
      grooming: "Grooming",
      dental: "Dental Care",
      "follow-up": "Follow-up",
    };
    return types[type] || type;
  };

  // Format date for display - FIXED VERSION
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Create date object using the date string directly with time to avoid timezone issues
    const date = new Date(dateString + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="w-full  mx-auto bg-white rounded-2xl shadow-xl border overflow-hidden">

      <div className="px-4 sm:px-6 py-6 sm:py-8 ">
        {/* Appointment Summary */}
        <div className=" mb-6">
          <h3 className="font-bold text-blue-800 mb-4 text-base sm:text-lg flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Appointment Summary
          </h3>

          <div className="my-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Pet Information */}
            <div className="bg-white p-3 sm:p-4 rounded-xl border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm sm:text-base truncate">
                    {petName}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {species} • {breed}
                  </p>
                </div>
              </div>
            </div>

            {/* Doctor Information */}
            <div className="bg-white p-3 sm:p-4 rounded-xl border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm sm:text-base truncate">
                    Dr. {doctorName}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {specialization[0] || "General Medicine"}
                  </p>
                </div>
              </div>
            </div>


          </div>
          {/* Appointment Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-xl border border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Stethoscope className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Type:</span>
                </div>
                <span className="font-semibold text-gray-800 text-sm">
                  {formatAppointmentType(type)}
                </span>
              </div>
            </div>

            {date && (
              <div className="bg-white p-3 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-gray-600">Date & Time</span>
                  </div>
                  <span className="font-semibold text-gray-800 text-sm text-right">
                    {formatDate(date)} - {time}
                  </span>
                </div>
              </div>
            )}

            {/* {time && (
                <div className="bg-white p-3 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-gray-600">Time:</span>
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">
                      {time}
                    </span>
                  </div>
                </div>
              )} */}
          </div>

          {/* Additional Notes */}
          {notes && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
                <span className="w-5 h-5 flex items-center justify-center mr-2">
                  📝
                </span>
                Additional Notes
              </h4>
              <p className="text-sm text-yellow-700 leading-relaxed">{notes}</p>
            </div>
          )}
        </div>

        {/* Payment Breakdown */}
        <div className="  p-4 sm:p-5 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 text-base sm:text-lg flex items-center">
            <CreditCard className="w-5 h-5 mr-2" />
            Payment Breakdown
          </h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="text-gray-700 font-medium text-sm">
                Consultation Fee:
              </span>
              <span className="font-bold text-gray-800">
                {consultationFee || 1}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="text-gray-700 font-medium text-sm">
                Service Charge:
              </span>
              <span className="font-bold text-gray-800">₹1</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="text-gray-700 font-medium text-sm">
                Payment Gateway:
              </span>
              <span className="font-bold text-gray-800 text-sm">Razorpay</span>
            </div>

            <div className="border-t-2 border-gray-300 pt-3">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-lg font-bold text-gray-800">
                  Total Amount:
                </span>
                <span className="text-xl sm:text-2xl font-bold text-blue-600">
                  ₹{amount}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Badge */}
        <div className="flex items-center justify-center mb-6 p-3 bg-green-50 border border-green-200 rounded-xl">
          <Shield className="w-5 h-5 text-green-600 mr-2" />
          <span className="text-sm text-green-700 font-medium">
            256-bit SSL Encrypted • PCI DSS Compliant
          </span>
        </div>

        {/* Pay Button */}
        <style jsx>{`
  @keyframes smoothPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 rgba(59, 130, 246, 0.0);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
    }
  }

  .smooth-pulse {
    animation: smoothPulse 2s ease-in-out infinite;
  }
`}</style>

        <button
          onClick={handlePayment}
          disabled={loading}
          className={`w-full py-3 sm:py-4 px-6 rounded-xl font-bold text-base sm:text-lg text-white transition-all duration-300 shadow-lg 
    ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 active:scale-95 smooth-pulse"
            }`}
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div>
              <span>Processing Payment...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
              <span>Pay ₹{amount} Securely</span>
            </div>
            
          )}
        </button>



        {/* Payment Methods */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-600 mb-2 font-medium">
            Accepted Payment Methods
          </p>
          <div className="flex justify-center space-x-2">
            <div className="w-10 h-6 sm:w-12 sm:h-7 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
              VISA
            </div>
            <div className="w-10 h-6 sm:w-12 sm:h-7 bg-red-500 rounded text-white text-xs flex items-center justify-center font-bold">
              MC
            </div>
            <div className="w-10 h-6 sm:w-12 sm:h-7 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">
              RUP
            </div>
            <div className="w-10 h-6 sm:w-12 sm:h-7 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">
              UPI
            </div>
          </div>
        </div>

        {/* Status Message */}
        {paymentStatus && (
          <div
            className={`mt-4 p-4 rounded-xl text-sm font-semibold border ${paymentStatus.includes("Successful")
              ? "bg-green-50 text-green-800 border-green-200"
              : paymentStatus.includes("Failed") ||
                paymentStatus.includes("verification failed")
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-yellow-50 text-yellow-800 border-yellow-200"
              }`}
          >
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 mt-0.5">
                {paymentStatus.includes("Successful") ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 flex items-center justify-center text-lg">
                    {paymentStatus.includes("Failed") ? "❌" : "⚠️"}
                  </div>
                )}
              </div>
              <p className="leading-relaxed">{paymentStatus}</p>
            </div>
          </div>
        )}

        {/* Payment Details */}
        {paymentData && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Payment Successful
            </h4>

            <div className="space-y-2 text-sm text-green-700">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-2 bg-white/60 rounded-lg">
                <span className="font-semibold">Payment ID:</span>
                <span className="font-mono text-xs break-all">
                  {paymentData.paymentId}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 p-2 bg-white/60 rounded-lg">
                <span className="font-semibold">Order ID:</span>
                <span className="font-mono text-xs break-all">
                  {paymentData.orderId}
                </span>
              </div>

              <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                <span className="font-semibold">Amount:</span>
                <span className="font-bold">₹{paymentData.amount}</span>
              </div>

              <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                <span className="font-semibold">Status:</span>
                <span className="font-bold text-green-600 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {paymentData.status}
                </span>
              </div>
            </div>
          </div>
        )}



        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center text-xs text-gray-500 space-x-1">
            <Shield className="w-4 h-4" />
            <span>Secured by Razorpay • Your payment information is safe</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentComponent;
